import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';
import { stripQuery } from './utils/stripQuery';

// Import the generated route tree
import { routeTree } from './routeTree.gen.ts';

import '@leoradating/ui/style.css';
import './styles.scss';
import reportWebVitals from './reportWebVitals.ts';
import { QueryClient } from '@tanstack/react-query';

// Initialize Sentry for error reporting and session replay
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      if (event.request?.url) event.request.url = stripQuery(event.request.url);
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (typeof breadcrumb.data?.url === 'string') breadcrumb.data.url = stripQuery(breadcrumb.data.url);
      if (typeof breadcrumb.data?.to === 'string') breadcrumb.data.to = stripQuery(breadcrumb.data.to);
      if (typeof breadcrumb.data?.from === 'string') breadcrumb.data.from = stripQuery(breadcrumb.data.from);
      return breadcrumb;
    },
  });
}

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient: new QueryClient(),
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById('app');
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
